import React, { useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';

import {
  useCreateActionRewardConfig,
  useUpdateActionRewardConfig,
} from '~/hooks/apis/loyalty/useActionRewardConfigMutations';

import ApiError from '~/types/ApiError';
import {
  ACTION_REWARD_CONFIG_STATUSES,
  ACTION_REWARD_EXCLUDE_COLUMNS,
  ACTION_REWARD_MIN_MAX_REPEAT,
  type ActionRewardConfig,
  type ActionRewardConfigStatus,
  type ActionRewardExcludeColumn,
  type CreateActionRewardConfigRequestBody,
  type UpdateActionRewardConfigRequestBody,
} from '~/types/loyaltyContract';

interface Props {
  open: boolean;
  onClose: () => void;
  config?: ActionRewardConfig;
}

interface FormState {
  actionName: string;
  title: string;
  description: string;
  activityDescription: string;
  rewardType: string;
  groupType: string;
  earnKey: string;
  allowRepeat: boolean;
  maxRepeat: string;
  excludeColumn: string;
  status: ActionRewardConfigStatus;
}

const EMPTY_STATE: FormState = {
  actionName: '',
  title: '',
  description: '',
  activityDescription: '',
  rewardType: '',
  groupType: '',
  earnKey: '',
  allowRepeat: false,
  maxRepeat: '',
  excludeColumn: '',
  status: 'ACTIVE',
};

const MAX_REPEAT_HELP = `Leave blank for unlimited claims. If set, must be a whole number of ${ACTION_REWARD_MIN_MAX_REPEAT} or more.`;
const MAX_REPEAT_ERROR = `Must be a whole number of ${ACTION_REWARD_MIN_MAX_REPEAT} or more, or left blank for unlimited.`;

function toFormState(config?: ActionRewardConfig): FormState {
  if (!config) {
    return EMPTY_STATE;
  }
  return {
    actionName: config.actionName,
    title: config.title,
    description: config.description,
    activityDescription: config.activityDescription,
    rewardType: config.rewardType,
    groupType: config.groupType ?? '',
    earnKey: config.earnKey,
    allowRepeat: config.allowRepeat,
    maxRepeat: config.maxRepeat != null ? String(config.maxRepeat) : '',
    excludeColumn: config.excludeColumn ?? '',
    status: config.status === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE',
  };
}

function helperText(submitted: boolean, error: string | null): string | undefined {
  return submitted && error ? error : undefined;
}

/** Create or update dialog for action reward configs. */
export default function ActionRewardConfigFormDialog({ open, onClose, config }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const isEdit = !!config;

  const [state, setState] = useState<FormState>(EMPTY_STATE);
  const [submitted, setSubmitted] = useState(false);
  const [actionNameConflict, setActionNameConflict] = useState<string | null>(null);
  const [confirmUnlimitedOpen, setConfirmUnlimitedOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setSubmitted(false);
    setActionNameConflict(null);
    setConfirmUnlimitedOpen(false);
    setState(toFormState(config));
  }, [open, config]);

  const trimmed = {
    actionName: state.actionName.trim(),
    title: state.title.trim(),
    description: state.description.trim(),
    activityDescription: state.activityDescription.trim(),
    rewardType: state.rewardType.trim(),
    groupType: state.groupType.trim(),
    earnKey: state.earnKey.trim(),
  };

  const maxRepeatInput = state.maxRepeat.trim();
  const isUnlimitedRepeat = state.allowRepeat && maxRepeatInput === '';
  const maxRepeatNumber = Number(maxRepeatInput);
  const maxRepeatValid =
    maxRepeatInput === '' || (Number.isInteger(maxRepeatNumber) && maxRepeatNumber >= ACTION_REWARD_MIN_MAX_REPEAT);

  const fieldErrors = {
    actionName: !trimmed.actionName
      ? 'Action name is required.'
      : trimmed.actionName.length > 100
        ? 'Action name must be 100 characters or fewer.'
        : null,
    title: !trimmed.title
      ? 'Title is required.'
      : trimmed.title.length > 50
        ? 'Title must be 50 characters or fewer.'
        : null,
    description: !trimmed.description
      ? 'Description is required.'
      : trimmed.description.length > 500
        ? 'Description must be 500 characters or fewer.'
        : null,
    activityDescription: !trimmed.activityDescription
      ? 'Activity description is required.'
      : trimmed.activityDescription.length > 500
        ? 'Activity description must be 500 characters or fewer.'
        : null,
    rewardType: !trimmed.rewardType
      ? 'Reward type is required.'
      : trimmed.rewardType.length > 50
        ? 'Reward type must be 50 characters or fewer.'
        : null,
    groupType: trimmed.groupType.length > 50 ? 'Group type must be 50 characters or fewer.' : null,
    earnKey: !trimmed.earnKey ? 'Earn key is required.' : null,
    maxRepeat: state.allowRepeat && !maxRepeatValid ? MAX_REPEAT_ERROR : null,
  };

  const errors = {
    ...fieldErrors,
    actionName: actionNameConflict ?? fieldErrors.actionName,
  };

  const hasFieldErrors = Object.values(fieldErrors).some(Boolean);

  const createMutation = useCreateActionRewardConfig({
    onSuccess: () => {
      enqueueSnackbar('Action reward config created', { variant: 'success' });
      onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 409) {
        const message = error.message || 'An action reward config with this action name already exists.';
        setActionNameConflict(message);
        enqueueSnackbar(message, { variant: 'error' });
        return;
      }
      enqueueSnackbar(`Failed to create config: ${error.message}`, { variant: 'error' });
    },
  });

  const updateMutation = useUpdateActionRewardConfig({
    onSuccess: () => {
      enqueueSnackbar('Action reward config updated', { variant: 'success' });
      onClose();
    },
    onError: (error) => {
      if (error instanceof ApiError && error.status === 404) {
        enqueueSnackbar(error.message || 'Action reward config not found', { variant: 'error' });
        onClose();
        return;
      }
      enqueueSnackbar(`Failed to update config: ${error.message}`, { variant: 'error' });
    },
  });

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  const submitPayload = () => {
    const excludeColumn = (
      state.excludeColumn.trim() === '' ? null : state.excludeColumn.trim()
    ) as CreateActionRewardConfigRequestBody['excludeColumn'];
    const maxRepeat = maxRepeatInput === '' ? null : maxRepeatNumber;

    if (isEdit && config) {
      const body: UpdateActionRewardConfigRequestBody = {
        rewardType: trimmed.rewardType,
        groupType: trimmed.groupType || null,
        title: trimmed.title,
        description: trimmed.description,
        activityDescription: trimmed.activityDescription,
        earnKey: trimmed.earnKey,
        allowRepeat: state.allowRepeat,
        excludeColumn,
        status: state.status,
        // Backend accepts null to clear a previous cap. Contract omits null on PATCH.
        maxRepeat: state.allowRepeat ? maxRepeat : null,
      };
      updateMutation.mutate({ id: config.id, body });
      return;
    }

    const body: CreateActionRewardConfigRequestBody = {
      actionName: trimmed.actionName,
      rewardType: trimmed.rewardType,
      title: trimmed.title,
      description: trimmed.description,
      activityDescription: trimmed.activityDescription,
      earnKey: trimmed.earnKey,
      allowRepeat: state.allowRepeat,
    };
    if (trimmed.groupType) {
      body.groupType = trimmed.groupType;
    }
    if (maxRepeat != null) {
      body.maxRepeat = maxRepeat;
    }
    if (excludeColumn) {
      body.excludeColumn = excludeColumn;
    }
    createMutation.mutate(body);
  };

  const handleSubmit = () => {
    setSubmitted(true);
    setActionNameConflict(null);
    if (hasFieldErrors) {
      return;
    }
    if (isUnlimitedRepeat) {
      setConfirmUnlimitedOpen(true);
      return;
    }
    submitPayload();
  };

  const handleConfirmUnlimited = () => {
    setConfirmUnlimitedOpen(false);
    submitPayload();
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    setConfirmUnlimitedOpen(false);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>{isEdit ? 'Edit action reward config' : 'Create action reward config'}</DialogTitle>
        <DialogContent sx={{ pt: 0.5 }}>
          <Stack spacing={1.25} mt={0.5}>
            <TextField
              size="small"
              label="Action name"
              value={state.actionName}
              onChange={(e) => {
                setActionNameConflict(null);
                setState({ ...state, actionName: e.target.value });
              }}
              required
              fullWidth
              disabled={isEdit || isSubmitting}
              error={submitted && !!errors.actionName}
              helperText={helperText(submitted, errors.actionName)}
            />
            <TextField
              size="small"
              label="Title"
              value={state.title}
              onChange={(e) => setState({ ...state, title: e.target.value })}
              required
              fullWidth
              disabled={isSubmitting}
              error={submitted && !!errors.title}
              helperText={helperText(submitted, errors.title)}
            />
            <TextField
              size="small"
              label="Description"
              value={state.description}
              onChange={(e) => setState({ ...state, description: e.target.value })}
              required
              fullWidth
              multiline
              minRows={2}
              disabled={isSubmitting}
              error={submitted && !!errors.description}
              helperText={helperText(submitted, errors.description)}
            />
            <TextField
              size="small"
              label="Activity description"
              value={state.activityDescription}
              onChange={(e) => setState({ ...state, activityDescription: e.target.value })}
              required
              fullWidth
              disabled={isSubmitting}
              error={submitted && !!errors.activityDescription}
              helperText={helperText(submitted, errors.activityDescription)}
            />
            <TextField
              size="small"
              label="Reward type"
              value={state.rewardType}
              onChange={(e) => setState({ ...state, rewardType: e.target.value })}
              required
              fullWidth
              disabled={isSubmitting}
              error={submitted && !!errors.rewardType}
              helperText={helperText(submitted, errors.rewardType)}
            />
            <TextField
              size="small"
              label="Earn key"
              value={state.earnKey}
              onChange={(e) => setState({ ...state, earnKey: e.target.value })}
              required
              fullWidth
              disabled={isSubmitting}
              error={submitted && !!errors.earnKey}
              helperText={helperText(submitted, errors.earnKey)}
            />
            <Stack spacing={1}>
              <Stack direction="row" spacing={2} alignItems="center">
                <FormControlLabel
                  sx={{ mr: 0, flexShrink: 0 }}
                  control={
                    <Switch
                      checked={state.allowRepeat}
                      onChange={(e) =>
                        setState({
                          ...state,
                          allowRepeat: e.target.checked,
                          maxRepeat: e.target.checked ? state.maxRepeat : '',
                        })
                      }
                      disabled={isSubmitting}
                      inputProps={{ 'aria-label': 'Allow repeat' }}
                    />
                  }
                  label="Allow repeat"
                />
                {state.allowRepeat && (
                  <TextField
                    size="small"
                    label="Max repeat"
                    value={state.maxRepeat}
                    onChange={(e) => setState({ ...state, maxRepeat: e.target.value })}
                    fullWidth
                    type="number"
                    disabled={isSubmitting}
                    error={submitted && !!errors.maxRepeat}
                    helperText={submitted && errors.maxRepeat ? errors.maxRepeat : MAX_REPEAT_HELP}
                    inputProps={{ min: ACTION_REWARD_MIN_MAX_REPEAT, step: 1 }}
                  />
                )}
              </Stack>
              {isUnlimitedRepeat && (
                <Alert severity="warning">
                  No max repeat. Members can claim this action as many times as they want.
                </Alert>
              )}
            </Stack>
            <TextField
              size="small"
              select
              label="Exclude column"
              value={state.excludeColumn}
              onChange={(e) => setState({ ...state, excludeColumn: e.target.value })}
              fullWidth
              disabled={isSubmitting}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {ACTION_REWARD_EXCLUDE_COLUMNS.map((column) => (
                <MenuItem key={column} value={column}>
                  {column}
                </MenuItem>
              ))}
              {state.excludeColumn &&
                !ACTION_REWARD_EXCLUDE_COLUMNS.includes(state.excludeColumn as ActionRewardExcludeColumn) && (
                  <MenuItem value={state.excludeColumn}>{state.excludeColumn}</MenuItem>
                )}
            </TextField>
            {isEdit && (
              <TextField
                size="small"
                select
                label="Status"
                value={state.status}
                onChange={(e) => setState({ ...state, status: e.target.value as ActionRewardConfigStatus })}
                fullWidth
                disabled={isSubmitting}
              >
                {ACTION_REWARD_CONFIG_STATUSES.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status === 'ACTIVE' ? 'Active' : 'Inactive'}
                  </MenuItem>
                ))}
              </TextField>
            )}
            <TextField
              size="small"
              label="Group type"
              value={state.groupType}
              onChange={(e) => setState({ ...state, groupType: e.target.value })}
              fullWidth
              disabled={isSubmitting}
              error={submitted && !!errors.groupType}
              helperText={helperText(submitted, errors.groupType)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={confirmUnlimitedOpen} onClose={() => setConfirmUnlimitedOpen(false)}>
        <DialogTitle>Unlimited claims</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Max repeat is empty, so members can claim this action with no cap. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="text" onClick={() => setConfirmUnlimitedOpen(false)} disabled={isSubmitting}>
            Go back
          </Button>
          <Button variant="contained" color="primary" onClick={handleConfirmUnlimited} disabled={isSubmitting}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
